<template>
  <div class="flex-grow-1" :data-testid="testId">
    <template v-if="readOnly">
      <div class="d-flex flex-wrap gap-2 mt-3">
        <v-chip
          :color="'dark-gray'"
          v-for="(refNumber, index) in modelValue"
          :key="`ref-num-${refNumber}`"
          class="mb-2"
          :class="{ 'mr-2': index + 1 < modelValue.length }">
          <span class="tag-value">{{ refNumber }}</span>
        </v-chip>
      </div>
    </template>
    <v-combobox
      v-else
      ref="comboboxref"
      :key="renderKey"
      @keydown="onKeydown"
      @paste="onPaste"
      variant="outlined"
      :multiple="true"
      :model-value="modelValue"
      v-model:search="search"
      :rules="rules"
      :delimiters="[',']"
      @update:model-value="emitInput"
      :label="label"
      hide-selected
      item-title="name"
      item-value="name"
      :return-object="false"
      :chips="hasChips"
      persistent-placeholder
      :placeholder="placeholder"
      :error-messages="errorMessages"
      :hide-no-data="hideInstructions"
      class="ref-num-input stack-selections label-text">
      <template #chip="{ item }">
        <v-chip class="color-neutral-40" label>
          <span class="pr-2 ref-num-value text-black">
            {{ item.raw }}
          </span>
          <v-icon
            size="small"
            class="text-black"
            v-if="allowRefNumberDelete"
            @click="removeRefNumber(item.raw)">
            mdi-close-circle
          </v-icon>
        </v-chip>
      </template>

      <template #no-data>
        <v-list-item class="input-instructions"><span v-html="defaultInstructions" /></v-list-item>
      </template>
    </v-combobox>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { getTranslatedRefNumberDisplayName } from '@nova/core';
import { t } from '@satellite/plugins/translator';

const {
  modelValue,
  label,
  allowRefNumberDelete,
  instructions,
  refNumberDisplayName,
  readOnly,
  placeholder,
  rules,
  itemRules,
  maxItems
} = defineProps({
  modelValue: {
    type: [Array, String],
    required: false,
    default: () => []
  },
  /**
   * Field Label
   */
  label: {
    type: String,
    required: false
  },
  /**
   * Allow deletion of individual ref numbers
   */
  allowRefNumberDelete: {
    type: Boolean,
    required: false,
    default: true
  },
  /**
   * Field instructions
   */
  instructions: {
    type: String,
    required: false
  },
  refNumberDisplayName: {
    type: String,
    required: false
  },
  readOnly: {
    type: Boolean,
    required: false,
    default: false
  },
  placeholder: {
    type: String,
    required: false,
    default: () => t('global.phrases.addReferenceNumber')
  },
  rules: {
    type: Array,
    required: false,
    default: () => []
  },
  itemRules: {
    type: Array,
    required: false,
    default: () => []
  },
  maxItems: {
    type: Number,
    required: false,
    default: Infinity
  },
  testId: {
    type: String,
    required: false,
    default: 'ref-num-input'
  }
});

const emit = defineEmits(['update:model-value']);
const errorMessages = ref([]);
const hideInstructions = computed(() => errorMessages.value.length > 0);
const renderKey = ref(0);

const search = ref('');

const comboboxref = ref(null);

const hasChips = computed(() => !!modelValue?.length);
const defaultInstructions = computed(
  () =>
    instructions ??
    t('forms.common.messages.pressEnterOrTabToActionEntity', {
      action: t('global.basicTerms.add').toLowerCase(),
      entity: getTranslatedRefNumberDisplayName(refNumberDisplayName).toLowerCase()
    })
);

const rerenderAndFocus = () => {
  nextTick(() => {
    renderKey.value++;
    const input = comboboxref.value?.$el?.querySelector('input');
    if (input) {
      input.focus();
    }
  });
};

const onKeydown = async e => {
  if (e.key === 'Tab' && search.value) {
    e.preventDefault();
    const currentValue = search.value;
    search.value = '';
    emitInput([...modelValue, currentValue]);
    await nextTick(() => rerenderAndFocus());
  }
};

const onPaste = async e => {
  const text = e.clipboardData?.getData('text');
  if (!text) {
    return;
  }

  e.preventDefault();
  const values = text
    .split(',')
    .map(v => v.trim())
    .filter(v => v);

  if (values.length) {
    search.value = '';
    emitInput([...modelValue, ...values]);
    await nextTick(() => rerenderAndFocus());
  }
};

const runItemRules = refNum => {
  const fns = itemRules.flat(Infinity).filter(fn => typeof fn === 'function');
  for (const fn of fns) {
    const res = fn([refNum]);
    if (res !== true) {
      return typeof res === 'string' ? res : 'Invalid value';
    }
  }
  return true;
};

const effectiveMaxItems = computed(() => {
  return maxItems === null ? Infinity : maxItems;
});

const emitInput = val => {
  const invalidRefNums = {};
  errorMessages.value = [];

  const seen = new Set();
  let uniqueVal = val.filter(item => {
    if (seen.has(item)) return false;
    seen.add(item);
    return true;
  });

  if (uniqueVal.length > effectiveMaxItems.value) {
    const removedCount = uniqueVal.length - effectiveMaxItems.value;
    val.slice(0, effectiveMaxItems.value);
    flashErrors([
      t(`global.messages.maximumOfReferenceNumbersAllowedCountRemoved`, {
        maxItems: effectiveMaxItems.value,
        removedCount
      })
    ]);
    return;
  }

  for (const refNum of uniqueVal) {
    const result = runItemRules(refNum);
    if (result !== true) {
      invalidRefNums[refNum] = result;
    }
  }
  if (
    Object.keys(invalidRefNums).length === 0 &&
    JSON.stringify(uniqueVal) !== JSON.stringify(modelValue)
  ) {
    emit('update:model-value', uniqueVal);
  } else {
    const messages = Object.entries(invalidRefNums).map(([key, val]) => {
      return `${key} ${val.toLowerCase()}`;
    });
    flashErrors(messages);
  }
};

// Flashes short-lived errors to user
// It gives enough time to see what number was invalid and why so they can re-enter
// Leaving errors indefinitely here is bad UX as they may decide they don't need additional numbers
let clearTimer;
const flashErrors = messagesArr => {
  clearTimeout(clearTimer);
  errorMessages.value = messagesArr;
  clearTimer = setTimeout(() => (errorMessages.value = []), 5000);
};

watch(search, val => {
  if (val) {
    errorMessages.value = [];
  }
});

const removeRefNumber = refNumber => {
  if (Array.isArray(modelValue)) {
    emitInput(modelValue.filter(item => item !== refNumber));
  } else {
    emitInput([]);
  }
};
</script>
